// src/pages/packingBatch/PrintFormats/Label102x76Format1.jsx
import React, { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import styles from './Label102x76Format1.module.css';
import { getImageUrl, handleImageError } from '../../../utils/imageUtils';

const Label102x76Format1 = ({ printData }) => {
  const barcodeRefs = useRef({});

  useEffect(() => {
    // Generate barcodes for all airwaybill numbers - optimized for thermal printing
    printData.orders.forEach((order) => {
      const barcodeElement = barcodeRefs.current[order.airwaybillNumber];
      if (barcodeElement && order.airwaybillNumber) {
        try {
          JsBarcode(barcodeElement, order.airwaybillNumber, {
            format: 'CODE128',
            width: 2.5,
            height: 45,
            displayValue: true,
            fontSize: 14,
            margin: 2,
            background: '#ffffff',
            lineColor: '#000000',
            fontOptions: 'bold'
          });
        } catch (error) {
          console.error('Failed to generate barcode:', error);
        }
      }
    });
  }, [printData]);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPhone = (phones) => {
    if (!phones || phones.length === 0) return 'N/A';
    return phones.join(', ');
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className={styles.labelsContainer}>
      {printData.orders.map((order, index) => (
        <div key={order.orderId} className={styles.label}>
          {/* Header Section */}
          <div className={styles.labelHeader}>
            <div className={styles.shopInfo}>
              {order.shop.logo ? (
                <img 
                  src={getImageUrl(order.shop.logo)} 
                  alt={order.shop.name}
                  className={styles.shopLogo}
                  onError={(e) => handleImageError(e)}
                />
              ) : (
                <div className={styles.shopLogoPlaceholder}>
                  {order.shop.name.charAt(0)}
                </div>
              )}
              <div className={styles.shopDetails}>
                <div className={styles.shopName}>{truncateText(order.shop.name, 20)}</div>
                {order.shop.phone && (
                  <div className={styles.shopPhone}>{order.shop.phone}</div>
                )}
              </div>
            </div>
            <div className={styles.courierInfo}>
              <div className={styles.courierName}>
                {printData.batch?.account?.name || printData.batch?.courier?.name || order?.courierAccount?.name || 'N/A'}
              </div>
            </div>
          </div>

          {/* Order Number & Date with URGENT */}
          <div className={styles.orderInfo}>
            <div className={styles.orderNumberGroup}>
              <div className={styles.orderNumber}>
                Order: #{order.orderNumber}
              </div>
              {order.isUrgent && (
                <div className={styles.urgentBadge}>URGENT</div>
              )}
            </div>
            <div className={styles.orderDate}>
              {formatDate(order.orderDate)}
            </div>
          </div>

          {/* Customer Section */}
          <div className={styles.customerSection}>
            <div className={styles.sectionLabel}>DELIVER TO</div>
            <div className={styles.customerName}>{order.customer.name}</div>
            <div className={styles.customerPhone}>
              📞 {formatPhone(order.customer.allPhones)}
            </div>
          </div>

          {/* Delivery Address */}
          <div className={styles.addressSection}>
            <div className={styles.address}>{truncateText(order.customer.address, 75)}</div>
            <div className={styles.city}>
              📍 {order.customer.city}
              {order.customer.district && ` (${order.customer.district} DIST.)`}
            </div>
          </div>

          {/* Order Items */}
          <div className={styles.itemsSection}>
            <div className={styles.sectionLabel}>
              ITEMS ({order.itemsCount})
            </div>
            <div className={styles.itemsList}>
              {order.items.slice(0, 4).map((item, idx) => (
                <div key={idx} className={styles.itemRow}>
                  <span className={styles.itemName}>{item.itemDisplayStr}</span>
                </div>
              ))}
              {order.items.length > 4 && (
                <div className={styles.moreItems}>
                  +{order.items.length - 4} more item(s)
                </div>
              )}
            </div>
          </div>

          {/* Delivery Notes */}
          {order.deliveryNotes && (
            <div className={styles.notesSection}>
              <div className={styles.sectionLabel}>NOTES</div>
              <div className={styles.notes}>
                {truncateText(order.deliveryNotes, 50)}
              </div>
            </div>
          )}

          {/* COD Amount */}
          <div className={styles.codSection}>
            <div className={styles.codLabel}>COD AMOUNT</div>
            <div className={styles.codAmount}>
              Rs. {order.financials.codAmount.toLocaleString()}
            </div>
          </div>

          {/* Barcode Section */}
          <div className={styles.barcodeSection}>
            <svg
              ref={(el) => (barcodeRefs.current[order.airwaybillNumber] = el)}
              className={styles.barcode}
            />
          </div>

          {/* Footer */}
          <div className={styles.labelFooter}>
            {printData.batch?.warehouse?.name || order?.warehouse?.name || ''}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Label102x76Format1;